import { ReactNode, useCallback, useEffect, useState } from 'react';
import { OperationOutcome, Patient } from '@medplum/fhirtypes';
import { PatientResourcesContext } from './PatientResourcesContext';
import { useConditionsData } from '@/hooks/useConditionsData';
import { useAllergiesData } from '@/hooks/useAllergiesData';
import { useMedicationsData } from '@/hooks/useMedicationsData';
import { useProceduresData } from '@/hooks/useProcedureData';
import { usePatient } from '@/hooks/usePatient';

interface PatientResourcesProviderProps {
  //The patient whose resources to load and subscribe to
  children: ReactNode;
  enableConditions?: boolean;
  enableAllergies?: boolean;
  enableMedications?: boolean;
  enableProcedures?: boolean;
}

export const PatientResourcesProvider = ({
  children,
  enableConditions = true,
  enableAllergies = true,
  enableMedications = true,
  enableProcedures = true,
}: PatientResourcesProviderProps) => {
  const [outcome, setOutcome] = useState<OperationOutcome>();
  const initialPatient = usePatient({ setOutcome });
  const [patient, setPatient] = useState<Patient | undefined>(initialPatient);

  useEffect(() => {
    if (initialPatient?.meta?.versionId) {
      setPatient(initialPatient);
    }
  }, [initialPatient]);

  const handlePatientUpdate = useCallback((updatedPatient: Patient) => {
    setPatient(updatedPatient);
  }, []);

  const {
    data: conditions,
    loading: conditionsLoading,
    refetch: refetchConditions,
  } = useConditionsData(patient, enableConditions);

  const {
    data: allergies,
    loading: allergiesLoading,
    refetch: refetchAllergies,
  } = useAllergiesData(patient, enableAllergies);

  const {
    data: medications,
    loading: medicationsLoading,
    refetch: refetchMedications,
  } = useMedicationsData(patient, enableMedications);

  const {
    data: procedures,
    loading: proceduresLoading,
    refetch: refetchProcedures,
  } = useProceduresData(patient, enableProcedures);

  return (
    <PatientResourcesContext.Provider
      value={{
        patient,
        outcome,
        handlePatientUpdate,
        conditions,
        allergies,
        medications,
        procedures,
        loading: {
          conditions: conditionsLoading,
          allergies: allergiesLoading,
          medications: medicationsLoading,
          procedures: proceduresLoading,
        },
        refetchConditions,
        refetchAllergies,
        refetchMedications,
        refetchProcedures,
      }}
    >
      {children}
    </PatientResourcesContext.Provider>
  );
};
